<template>
    <DsMultiselect
        v-model="contact"
        class="contact-multiselect"
        searchable
        help-text
        label-prop="fullName"
        help-text-prop="emailAddress"
        value-prop="id"
        :placeholder="$t('unlayerBuilder.sendTestEmail.contactForResolvingMergeFieldsInput.placeholder')"
        :options="options"
        :loading="isLoading"
        @updateSearch="onSearch"
        @blur="onBlur"
    >
        <template #help>
            <span class="help-text">
                {{ $t('unlayerBuilder.sendTestEmail.contactForResolvingMergeFieldsInput.helpText') }}
            </span>
        </template>
    </DsMultiselect>
</template>

<script>
import debounce from 'lodash.debounce';
import sentry from '@/analytics/sentry';
import { openSearch } from '@/api/shared';
import { SEARCHING_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import { fetchInitialContactListForTestEmails } from '@/api/campaigns/campaignUnlayerEmail';

export default {
    name: 'ContactForResolvingMergeFields',

    data() {
        return {
            defaultContactList: [],
            options: [],
            searchTerm: null,
            contact: null,
            isLoading: false,
        };
    },

    created() {
        this.fetchInitialContacts();
        this.onSearch = debounce(this.onSearch, SEARCHING_DEBOUNCE_DELAY);
    },

    watch: {
        contact(val) {
            if (!val) return;

            this.$emit('selected', val);
        }
    },

    methods: {
        onBlur() {
            if (this.options.length === 0) {
                this.options = this.defaultContactList;
            }
        },

        async onSearch(term) {
            if (!term) return;

            this.isLoading = true;

            try {
                const { queryResults } = await openSearch({
                    documentTypes: 'contact',
                    term,
                    highlightEnabled: false,
                });

                this.options = queryResults
                    .map(({ data: { email, fullName }, id }) => ({
                        emailAddress: email || '(No email)',
                        fullName: fullName || '(No name)',
                        id,
                    }));
            } catch (e) {
                sentry.captureException(new Error('Failed to search for contacts', { cause: e }));
            } finally {
                this.isLoading = false;
            }
        },

        async fetchInitialContacts() {
            this.isLoading = true;

            try {
                const data = await fetchInitialContactListForTestEmails();

                const noNameLabel = this.$t('unlayerBuilder.sendTestEmail.contactForResolvingMergeFieldsInput.noName');
                const noEmailLabel = this.$t('unlayerBuilder.sendTestEmail.contactForResolvingMergeFieldsInput.noEmail');

                this.defaultContactList = data
                    .map((c) => {
                        let name = '';

                        if (c.givenName) {
                            name = c.givenName;
                        }

                        if (c.middleName) {
                            name = `${name} ${c.middleName}`;
                        }

                        if (c.familyName) {
                            name = `${name} ${c.familyName}`;
                        }

                        return {
                            fullName: name,
                            emailAddress: c.emailAddress,
                            id: c.id,
                        };
                    })
                    .filter((c) => c.fullName || c.emailAddress)
                    .map((c) => ({
                        id: c.id,
                        fullName: c.fullName || noNameLabel,
                        emailAddress: c.emailAddress || noEmailLabel,
                    }));
                this.options = this.defaultContactList;
            } catch (e) {
                sentry.captureException(new Error('Failed to load initial list of contacts for test emails', { cause: e }));
            } finally {
                this.isLoading = false;
            }
        }
    },
}
</script>

<style lang="scss" scoped>
.contact-multiselect {
    margin-bottom: $spacing-400;
}

.help-text {
    position: absolute;
}
</style>
